import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, type NavigateFunction } from 'react-router-dom';
import type { GalleryAsset, Rating } from '@xphoto/shared';
import { useAuth, canWriteFor } from '../auth';
import { LIBRARY_ID } from '../gallery/config';
import { stateToFilters } from '../gallery/state';
import { getSortPrefs } from '../gallery/default-sort-pref';
import { accountRandomSeed } from '../gallery/random-seed';
import { resolveGalleryHref } from '../gallery/sort-resolution';
import { useGalleryViewerContext } from '../gallery/viewer-context';
import { Viewer, type ViewerPosition } from './Viewer';
import { TagWalk } from '../tagging';
import { useColdAsset } from './use-cold-asset';
import { loadSeriesSession, storeSeriesSession } from './series-session';
import { useViewerNavigation } from './use-viewer-navigation';

/**
 * Route de la VISIONNEUSE (`/photo/:id`) — « tout dans l'URL ».
 *
 * Ouvre la visionneuse sur l'asset `:id`, ADRESSABLE À FROID. Deux MODES de
 * navigation préc./suiv., selon l'état de route :
 *
 *  1. MODE GALERIE (défaut) — l'ensemble ordonné est celui de la galerie (mêmes
 *     tri/filtres, dérivés de `state.back`). On résout l'INDEX ABSOLU de l'asset (via
 *     la grille si chargé, sinon via l'endpoint de position `use-viewer-navigation`)
 *     puis on navigue vers l'id du voisin. Préchargement directionnel (cf.
 *     `use-viewer-navigation`).
 *
 *  2. MODE SÉRIE (`state.series` = liste ordonnée d'ids) — la visionneuse feuillette
 *     UNIQUEMENT cette liste explicite (ex. un groupe de doublons présenté comme sa
 *     propre série). L'index/total viennent de la position dans le tableau (aucun
 *     appel position/fenêtre), l'asset affiché est chargé à froid par id, et chaque
 *     saut RE-PASSE `{ back, series }` pour que le mode persiste. À froid (état de
 *     route perdu au F5), on dégrade proprement au mode galerie / asset seul.
 *
 * L'asset AFFICHÉ vient de la grille si elle le connaît (mode galerie à chaud, aucune
 * requête), sinon de son chargement à froid par id (`use-cold-asset`). Fermer revient
 * au chemin mémorisé (`state.back`) en préservant les filtres.
 */
interface ViewerRouteState {
  /** Chemin + query d'où l'on vient (fermeture = retour aux filtres / à l'outil). */
  back?: string;
  /**
   * Série ORDONNÉE d'ids à feuilleter (mode série). Non vide ⇒ la navigation reste
   * bornée à cette liste (ignore l'ensemble galerie). `undefined`/vide ⇒ mode galerie.
   */
  series?: string[];
  /**
   * Mode d'affichage. `'tag'` = TAGUEUR (image zoomable + panneau de tags à droite,
   * « Valider et suivant ») au lieu de la visionneuse normale ; exige une série (sinon
   * ignoré). Re-passé à chaque saut pour persister. Absent ⇒ visionneuse classique.
   */
  mode?: 'tag';
  /**
   * Démarre le DIAPORAMA automatiquement à l'ouverture (« Diaporama » sur une
   * sélection). Signal one-shot au MONTAGE de la visionneuse ; non re-passé aux sauts
   * (l'état de lecture persiste ensuite dans la visionneuse). Ne survit pas au F5
   * (état de route perdu) ⇒ dégradation propre en visionneuse normale.
   */
  slideshow?: boolean;
}

/**
 * Ouvre la visionneuse sur `startId` en MODE TAGUEUR (« Réviser / valider les tags ») : la
 * navigation reste bornée à la liste ORDONNÉE `ids`, chaque photo s'ouvre dans le tagueur
 * (image zoomable + panneau de tags), et `back` est le chemin de retour (× / Échap).
 * Contrat de l'état de route posé : `{ back, series: ids, mode: 'tag' }`.
 *
 * No-op si `ids` est vide ou si `startId` n'appartient pas à `ids` (garde-fou).
 */
export function openViewerTagging(
  navigate: NavigateFunction,
  ids: string[],
  startId: string,
  back: string,
): void {
  if (ids.length === 0 || !ids.includes(startId)) return;
  const state: ViewerRouteState = { back, series: ids, mode: 'tag' };
  navigate(`/photo/${startId}`, { state });
}

/**
 * Ouvre la visionneuse sur `startId` en MODE SÉRIE : la navigation préc./suiv. reste
 * bornée à la liste ORDONNÉE `ids`, et `back` est le chemin de retour (× / Échap /
 * clic-hors). Contrat de l'état de route posé : `{ back, series: ids }`.
 *
 * Consommé par le lot dédoublonnage (Lot D) pour ouvrir un groupe de doublons comme
 * sa propre série depuis un clic sur une vignette :
 *   `openViewerSeries(navigate, group.assetIds, clickedId, '/outils/dedup')`
 *
 * No-op si `ids` est vide ou si `startId` n'appartient pas à `ids` (garde-fou).
 */
export function openViewerSeries(
  navigate: NavigateFunction,
  ids: string[],
  startId: string,
  back: string,
): void {
  if (ids.length === 0 || !ids.includes(startId)) return;
  const state: ViewerRouteState = { back, series: ids };
  navigate(`/photo/${startId}`, { state });
}

/**
 * Ouvre la visionneuse sur `startId` en MODE SÉRIE avec DIAPORAMA démarré d'emblée :
 * la navigation reste bornée à la liste ORDONNÉE `ids`, la lecture automatique
 * commence à l'ouverture, et `back` est le chemin de retour (× / Échap / clic-hors).
 * Contrat de l'état de route posé : `{ back, series: ids, slideshow: true }`.
 *
 * Consommé par l'entrée « Diaporama » du menu « Actions… » d'une sélection :
 *   `openViewerSlideshow(navigate, [...selected], startId, galleryLinkFor(state))`
 *
 * No-op si `ids` est vide ou si `startId` n'appartient pas à `ids` (garde-fou).
 */
export function openViewerSlideshow(
  navigate: NavigateFunction,
  ids: string[],
  startId: string,
  back: string,
): void {
  if (ids.length === 0 || !ids.includes(startId)) return;
  const state: ViewerRouteState = { back, series: ids, slideshow: true };
  navigate(`/photo/${startId}`, { state });
}

export function ViewerRoute(): React.JSX.Element | null {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const gallery = useGalleryViewerContext();
  const { user } = useAuth();
  const libraryId = user?.libraryId ?? LIBRARY_ID;

  const assetId = id ?? null;

  // État de route « à chaud » (posé par `navigate`). `null` = entrée À FROID : F5,
  // lien collé, onglet restauré — `history.state` est perdu.
  const liveState = location.state as ViewerRouteState | null;

  // REPRISE DE SÉRIE APRÈS RECHARGEMENT (audit C1). Quand l'état de route est absent, on
  // relit la réserve `sessionStorage` : un F5 au milieu d'une session de tagage de 300
  // photos reprend la série au lieu de retomber en visionneuse classique sans recours.
  // On NE consulte la réserve que si `location.state` est ENTIÈREMENT absent : une
  // ouverture normale depuis la galerie pose toujours `{ back }`, et ne doit donc jamais
  // se voir greffer une série d'une session précédente.
  const restoredState = useMemo<ViewerRouteState | null>(() => {
    if (liveState !== null || assetId === null) return null;
    const saved = loadSeriesSession(assetId);
    if (saved === null) return null;
    return { back: saved.back, series: saved.series, ...(saved.mode ? { mode: saved.mode } : {}) };
  }, [liveState, assetId]);

  const routeState = liveState ?? restoredState;

  // Filtres API dérivés de l'URL de retour (même ensemble ordonné que la galerie).
  // La query galerie vit dans `state.back` (mémorisé à l'ouverture) ; à froid (lien
  // copié-collé sans `back`), on retombe sur `/`.
  //
  // La résolution passe par le MÊME module que `GalleryView` (`sort-resolution.ts`),
  // alimenté par les DEUX préférences de tri (accueil / navigation — c'est le chemin de
  // `back` qui décide) et la graine du COMPTE. C'est indispensable depuis que l'URL ne
  // porte plus la graine : la dériver de la seule query donnerait ici un ensemble ordonné
  // DIFFÉRENT de la grille (aléatoire sans graine, ou tri neutre au lieu de la
  // préférence) et préc./suiv. sauterait à côté — y compris sur un lien reçu à froid.
  const backTo = routeState?.back ?? '/';
  const accountSeed = accountRandomSeed(user?.randomSeed);
  const filters = useMemo(
    () => stateToFilters(resolveGalleryHref(backTo, { ...getSortPrefs(), accountSeed })),
    [backTo, accountSeed],
  );

  // MODE SÉRIE : liste ordonnée d'ids explicite (ex. groupe de doublons). Non vide ⇒
  // la navigation reste bornée à cette liste ; on n'interroge NI position NI fenêtre.
  const rawSeries = routeState?.series;
  const series = useMemo(
    () => (Array.isArray(rawSeries) && rawSeries.length > 0 ? rawSeries : null),
    [rawSeries],
  );
  const seriesIndex = series && assetId ? series.indexOf(assetId) : -1;
  const inSeries = series !== null && seriesIndex >= 0;
  // Mode TAGUEUR : requiert une série active (à froid, l'état de route perdu ⇒ retour
  // au mode visionneuse classique) ET l'accès ÉCRITURE. Un LECTEUR (bénéficiaire de lien
  // d'accès) qui atterrit sur un `mode: 'tag'` (lien périmé/copié) est dégradé en
  // visionneuse classique : le Tagueur est une affordance d'écriture, jamais exposée en
  // lecture seule.
  const canWrite = canWriteFor(user);
  const mode = routeState?.mode;
  const tagMode = mode === 'tag' && inSeries && canWrite;

  // Réserve la série courante pour survivre à un rechargement (audit C1). Écrite à
  // chaque saut : `history.state` est structured-cloné par le navigateur, donc l'identité
  // du tableau change de toute façon d'un saut à l'autre. Le coût (une sérialisation
  // JSON de quelques kilo-octets) est sans commune mesure avec les requêtes réseau que
  // chaque saut émet déjà. Une série absente laisse la réserve précédente en place : son
  // garde `assetId ∈ series` suffit à l'empêcher de s'appliquer ailleurs.
  useEffect(() => {
    if (series === null) return;
    storeSeriesSession({ back: backTo, series, ...(mode ? { mode } : {}) });
  }, [series, mode, backTo]);

  // Index/total connus de la grille (ouverture à chaud) — évite l'appel position.
  // En mode série on ne relie JAMAIS la grille (l'ensemble est la série, pas la galerie).
  const gridIndex = useMemo(
    () => (!inSeries && gallery && assetId ? gallery.indexOfId(assetId) : -1),
    [inSeries, gallery, assetId],
  );
  const inGrid = gridIndex >= 0;
  const knownIndex = inGrid ? gridIndex : null;
  const knownTotal = gallery ? gallery.total : null;

  // Navigation galerie : inerte en mode série (`assetId = null` → aucune requête
  // position/fenêtre), pilotée par la série sinon.
  const nav = useViewerNavigation({
    libraryId,
    assetId: inSeries ? null : assetId,
    filters,
    knownIndex,
    knownTotal,
    ...(!inSeries && gallery ? { gridAsset: gallery.getAsset } : {}),
  });

  const close = useCallback(() => {
    navigate(backTo);
  }, [navigate, backTo]);

  // Navigue vers un id voisin en conservant l'état de retour. En mode série, RE-PASSE
  // `series` pour que le mode persiste d'un saut à l'autre.
  const goToId = useCallback(
    (neighbourId: string) => {
      const nextState: ViewerRouteState = series
        ? { back: backTo, series, ...(mode ? { mode } : {}) }
        : { back: backTo };
      navigate(`/photo/${neighbourId}`, { replace: true, state: nextState });
    },
    [navigate, backTo, series, mode],
  );

  // Bornes/voisins : série d'abord (position dans le tableau), sinon navigation galerie.
  const canPrev = inSeries ? seriesIndex > 0 : nav.canPrev;
  const canNext = inSeries ? seriesIndex < (series?.length ?? 0) - 1 : nav.canNext;
  const prevId = inSeries ? (series?.[seriesIndex - 1] ?? null) : nav.prevId;
  const nextId = inSeries ? (series?.[seriesIndex + 1] ?? null) : nav.nextId;

  const onPrev = useCallback(() => {
    if (!canPrev || prevId === null) return;
    if (!inSeries) nav.notifyPrev();
    goToId(prevId);
  }, [canPrev, prevId, inSeries, nav, goToId]);

  const onNext = useCallback(() => {
    if (!canNext || nextId === null) return;
    if (!inSeries) nav.notifyNext();
    goToId(nextId);
  }, [canNext, nextId, inSeries, nav, goToId]);

  const onRatingChange = useCallback(
    (aId: string, rating: Rating) => {
      gallery?.onRatingChange(aId, rating);
    },
    [gallery],
  );

  // Asset affiché : grille (à chaud, sans requête) sinon chargement à froid par id.
  const cold = useColdAsset(!inGrid ? assetId : null);

  if (!assetId) return null;

  let asset: GalleryAsset | undefined;
  if (inGrid && gallery) {
    asset = gallery.getAsset(gridIndex);
  } else {
    asset = cold.asset ?? undefined;
  }

  // ÉCHEC du chargement à froid (audit F1). Sans cette propagation, `asset` restait
  // `undefined` et la visionneuse affichait « Chargement… » INDÉFINIMENT : ouvrir
  // `/photo/<id-supprimé>`, un lien partagé hors périmètre (403) ou simplement avec le
  // réseau coupé ne donnait AUCUN signal. La nature de l'erreur (404 / 403 / réseau)
  // est calculée par `useColdAsset` et pilote un message distinct.
  const loadError = !inGrid && cold.status === 'error' ? cold.errorKind : null;

  // Compteur : en mode série, position dans le tableau ; sinon dès que l'index absolu
  // est résolu (grille OU endpoint position).
  const position: ViewerPosition | null = inSeries
    ? { index: seriesIndex, total: series?.length ?? 0 }
    : nav.index !== null && nav.total > 0
      ? { index: nav.index, total: nav.total }
      : null;

  // MODE TAGUEUR : image zoomable + panneau de tags (« Réviser / valider les tags »).
  if (tagMode) {
    return (
      <TagWalk
        asset={asset}
        position={position}
        onPrev={onPrev}
        onNext={onNext}
        canPrev={canPrev}
        canNext={canNext}
        nextId={nextId}
        onClose={close}
      />
    );
  }

  // Drapeau « Diaporama » (posé par `openViewerSlideshow`) : démarre la lecture auto
  // au montage de la visionneuse. One-shot ; non re-passé aux sauts (`goToId`).
  const autoPlaySlideshow = routeState?.slideshow === true;

  return (
    <Viewer
      asset={asset}
      loadError={loadError}
      // GRAINE de détail (audit A1) : uniquement le détail chargé À FROID ici. En
      // contexte galerie à chaud, l'asset vient de la grille et personne n'a chargé
      // de détail → `null` (la visionneuse le chargera si un panneau s'ouvre).
      detail={!inGrid ? cold.detail : null}
      position={position}
      onPrev={onPrev}
      onNext={onNext}
      canPrev={canPrev}
      canNext={canNext}
      onClose={close}
      onRatingChange={onRatingChange}
      autoPlaySlideshow={autoPlaySlideshow}
    />
  );
}
